import React, { useState } from "react";
import { Mail, Lock, Eye, ShieldCheck } from "lucide-react";

const CreateAccountStep = ({ form, setForm, error, handleEmailBlur, emailExists, checkingEmail }) => {
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const isValidEmail = (email) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const email = form?.shopEmail || "";
    const emailValid = email.length > 0 && isValidEmail(email);

    return (
        <div className="bg-white rounded-[16px] border border-[#e4e5e7] p-[24px] flex flex-col gap-[20px]">

            {/* Email */}
            <div className="flex flex-col gap-[6px]">

                {
                    error && (
                        <div className="bg-[#fff0f0] border border-[#ffcdd2] rounded-[10px] px-[16px] py-[12px]">
                            <p className="font-medium text-[13px] text-[#d32f2f]">
                                {error || "An error occurred while creating your account. Please try again."}
                            </p>
                        </div>
                    )
                }


                <label className="font-semibold text-[14px] text-[#212326]">
                    Login Email
                </label>

                <div className="relative">
                    <Mail className="absolute left-[14px] top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-[#9298a0]" />

                    <input
                        type="email"
                        placeholder="your@email.com"
                        value={email}
                        onChange={(e) =>
                            setForm({ ...form, shopEmail: e.target.value })
                        }
                        className={`w-full h-[48px] rounded-[12px] border pl-[42px] pr-[16px] font-medium text-[14px] text-[#212326] placeholder:text-[#9298a0] focus:outline-none transition-colors ${email.length === 0
                            ? "border-[#e4e5e7] focus:border-[#ff5323]"
                            : emailValid
                                ? "border-[#2ecc71] focus:border-[#2ecc71]"
                                : "border-[#e74c3c] focus:border-[#e74c3c]"
                            }`}
                        onBlur={handleEmailBlur}
                    />
                </div>

                {/* Email status */}
                {checkingEmail ? (
                    <p className="font-medium text-[12px] text-[#9298a0]">
                        Checking email...
                    </p>
                ) : emailExists ? (
                    <p className="font-medium text-[12px] text-[#e74c3c]">
                        An account with this email already exists.
                    </p>
                ) : email.length === 0 ? (
                    <p className="font-medium text-[12px] text-[#9298a0]">
                        Pre-filled from your business contact email
                    </p>
                ) : !emailValid ? (
                    <p className="font-medium text-[12px] text-[#e74c3c]">
                        Please enter a valid email.
                    </p>
                ) : (
                    <p className="font-medium text-[12px] text-[#2ecc71]">
                        Email is valid.
                    </p>
                )}
            </div>

            {/* Password */}
            <div className="flex flex-col gap-[6px]">
                <label className="font-semibold text-[14px] text-[#212326]">
                    Password
                </label>

                <div className="relative">
                    <Lock className="absolute left-[14px] top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-[#9298a0]" />

                    <input
                        type={showPassword ? "text" : "password"}
                        placeholder="Create a password (min. 6 characters)"
                        value={form?.password || ""}
                        onChange={(e) =>
                            setForm({ ...form, password: e.target.value })
                        }
                        className="w-full h-[48px] rounded-[12px] border pl-[42px] pr-[48px] font-medium text-[14px] text-[#212326] placeholder:text-[#9298a0] focus:outline-none border-[#e4e5e7] focus:border-[#ff5323]"
                    />

                    <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-[14px] top-1/2 -translate-y-1/2 text-[#9298a0]"
                    >
                        {showPassword ? (
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-eye-off w-[18px] h-[18px]">
                                <path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49"></path>
                                <path d="M14.084 14.158a3 3 0 0 1-4.242-4.242"></path>
                                <path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143"></path>
                                <path d="m2 2 20 20"></path>
                            </svg>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-eye w-[18px] h-[18px]">
                                <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"></path>
                                <circle cx="12" cy="12" r="3"></circle>
                            </svg>
                        )}
                    </button>
                </div>
            </div>

            {/* Confirm Password */}
            <div className="flex flex-col gap-[6px]">
                <label className="font-semibold text-[14px] text-[#212326]">
                    Confirm Password
                </label>

                <div className="relative">
                    <Lock className="absolute left-[14px] top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-[#9298a0]" />

                    <input
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Re-enter your password"
                        value={form?.confirmPassword || ""}
                        onChange={(e) =>
                            setForm({ ...form, confirmPassword: e.target.value })
                        }
                        className="w-full h-[48px] rounded-[12px] border pl-[42px] pr-[48px] font-medium text-[14px] text-[#212326] placeholder:text-[#9298a0] focus:outline-none border-[#e4e5e7] focus:border-[#ff5323]"
                    />

                    <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-[14px] top-1/2 -translate-y-1/2 text-[#9298a0]"
                    >
                        {showConfirmPassword ? (
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-eye-off w-[18px] h-[18px]">
                                <path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49"></path>
                                <path d="M14.084 14.158a3 3 0 0 1-4.242-4.242"></path>
                                <path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143"></path>
                                <path d="m2 2 20 20"></path>
                            </svg>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-eye w-[18px] h-[18px]">
                                <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"></path>
                                <circle cx="12" cy="12" r="3"></circle>
                            </svg>
                        )}
                    </button>
                </div>
            </div>

            {/* Security Box */}
            <div className="flex items-start gap-[12px] bg-[#fafafa] rounded-[12px] border border-[#e4e5e7] px-[20px] py-[16px]">
                <ShieldCheck className="w-[20px] h-[20px] text-[#ff5323] shrink-0 mt-[1px]" />

                <div>
                    <p className="font-semibold text-[13px] text-[#212326] mb-[2px]">
                        Your account is secure
                    </p>

                    <p className="font-medium text-[12px] text-[#717182] leading-[1.5]">
                        These credentials will be used to log in to your store dashboard where
                        you can manage incoming print orders, update your shop profile, and
                        track activity.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default CreateAccountStep;